import axios from "axios";
import { API_BASE_PATH } from "../environment";
import { toast } from "react-toastify";

// ✅ Auth header generator
const getAuthHeaders = () => {
  const token = localStorage.getItem("authToken");
  return {
    Authorization: token,
    accept: "application/json",
  };
};

const handleAxiosError = (
  error: any,
  fallbackMessage = "Something went wrong"
) => {
  if (axios.isAxiosError(error)) {
    const message =
      error.response?.data?.details ||
      error.response?.data?.message ||
      fallbackMessage;

    toast.error(message);
  } else {
    toast.error(fallbackMessage);
  }
};

// ✅ Interface
export interface CustomerPotential {
  customerPotential_id: string;
  customerPotential_name: string;
  customerPotential_status: string;
  created_at: string;
  client: {
    client_id: string;
  };
}

// ✅ GET: Fetch all customer potentials
export const fetchAllCustomerPotentials = async (): Promise<CustomerPotential[]> => {
  try {
    const response = await axios.get(`${API_BASE_PATH}/customerPotential/`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to fetch customer potentials.";
    toast.error(errorMessage);
    return [];
  }
};
// ✅ GET: Fetch all customer potentials
export const fetchAllCustomerPotentialsActive = async (): Promise<CustomerPotential[]> => {
  try {
    const response = await axios.get(`${API_BASE_PATH}/customerPotential/active/`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to fetch customer potentials";
    toast.error(errorMessage);
    return [];
  }
};

// ✅ POST: Create customer potential
export const createCustomerPotential = async (
  name: string,
  status: string = "Active"
): Promise<CustomerPotential | null> => {
  try {
    const formData = new URLSearchParams();
    formData.append("customerPotential_name", name);
    formData.append("customerPotential_status", status);

    const response = await axios.post(
      `${API_BASE_PATH}/customerPotential/create`,
      formData,
      {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/x-www-form-urlencoded",
        },
      }
    );
    toast.success("Customer Potential created successfully");
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to create customer potential";
    toast.error(errorMessage);
    return null;
  }
};

// ✅ PUT: Update customer potential
export const updateCustomerPotential = async (
  id: string,
  name: string,
  status: string
): Promise<CustomerPotential | null> => {
  try {
    const formData = new URLSearchParams();
    formData.append("customerPotential_name", name);
    formData.append("customerPotential_status", status);

    const response = await axios.put(
      `${API_BASE_PATH}/customerPotential/update/${id}`,
      formData,
      {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/x-www-form-urlencoded",
        },
      }
    );
    toast.success("Customer Potential updated successfully");
    return response.data;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to update customer potential";
    toast.error(errorMessage);
    return null;
  }
};

// ✅ DELETE: Remove customer potential
export const deleteCustomerPotential = async (id: string): Promise<void> => {
  try {
    await axios.delete(`${API_BASE_PATH}/customerPotential/delete/${id}`, {
      headers: getAuthHeaders(),
    });
    toast.success("Customer Potential deleted");
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.details || error?.response?.data?.message || "Failed to delete customer potential";
    toast.error(errorMessage);
  }
};


/* =====================================================
   EXPORT – ALL
===================================================== */

// ✅ Export all Customer Types – Excel
export const exportCustomerTypeExcel = async (): Promise<Blob> => {
  try {
    const res = await axios.get(
      `${API_BASE_PATH}/customerPotential_export_all_excel`,
      {
        responseType: "blob",
        headers: getAuthHeaders(),
      }
    );

    toast.success("Customer types exported as Excel successfully");
    return res.data;
  } catch (error) {
    handleAxiosError(error, "Failed to export customer types (Excel)");
    throw error;
  }
};

// ✅ Export all Customer Types – PDF
export const exportCustomerTypePDF = async (): Promise<Blob> => {
  try {
    const res = await axios.get(
      `${API_BASE_PATH}/customerPotential_export_all_pdf`,
      {
        responseType: "blob",
        headers: getAuthHeaders(),
      }
    );

    toast.success("Customer types exported as PDF successfully");
    return res.data;
  } catch (error) {
    handleAxiosError(error, "Failed to export customer types (PDF)");
    throw error;
  }
};

/* =====================================================
   EXPORT – SELECTED
===================================================== */

// ✅ Export selected Customer Types – Excel
export const exportSelectedCustomerTypeExcel = async (
  ids: string[]
): Promise<Blob> => {
  try {
    const res = await axios.get(
      `${API_BASE_PATH}/customerPotential_export_selected_excel`,
      {
        params: { ids: ids.join(",") },
        responseType: "blob",
        headers: getAuthHeaders(),
      }
    );

    toast.success("Selected customer types exported as Excel successfully");
    return res.data;
  } catch (error) {
    handleAxiosError(
      error,
      "Failed to export selected customer types (Excel)"
    );
    throw error;
  }
};

// ✅ Export selected Customer Types – PDF
export const exportSelectedCustomerTypePDF = async (
  ids: string[]
): Promise<Blob> => {
  try {
    const res = await axios.get(
      `${API_BASE_PATH}/customerPotential_export_selected_pdf`,
      {
        params: { ids: ids.join(",") },
        responseType: "blob",
        headers: getAuthHeaders(),
      }
    );

    toast.success("Selected customer types exported as PDF successfully");
    return res.data;
  } catch (error) {
    handleAxiosError(
      error,
      "Failed to export selected customer types (PDF)"
    );
    throw error;
  }
};

/* =====================================================
   BULK DELETE
===================================================== */

// ✅ Bulk delete Customer Types
export const deleteCustomerTypeBulk = async (
  ids: string[]
): Promise<void> => {
  try {
    const res = await axios.delete(
      `${API_BASE_PATH}/customerPotential_bulk_delete`,
      {
        params: { ids: ids.join(",") },
        headers: getAuthHeaders(),
      }
    );

    // toast.success(
    //   res.data?.message || "Customer types deleted successfully"
    // );
  } catch (error: any) {
    if (error?.response?.status === 403) {
      console.error("You are not authorized to delete customer types.");
    } else {
      handleAxiosError(error, "Failed to delete customer types");
    }
    throw error;
  }
};

/* =====================================================
   IMPORT – EXCEL
===================================================== */

export interface CustomerTypeImportResult {
  message: string;
  added: {
    customerPotential_name: string;
    customerPotential_status: string;
  }[];
  skipped: {
    row: number;
    customerPotential_name: string;
    reason: string;
  }[];
}

// ✅ Import Customer Type Excel
export const importCustomerTypeExcel = async (
  file: File
): Promise<CustomerTypeImportResult> => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const res = await axios.post(
      `${API_BASE_PATH}/customerPotential_import_excel`,
      formData,
      {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "multipart/form-data",
        },
      }
    );

    toast.success(
      res.data?.message || "Customer type import completed"
    );
    return res.data;
  } catch (error) {
    handleAxiosError(error, "Failed to import customer types");
    throw error;
  }
};
